import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  Output,
  Type,
  ViewChild,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BreakpointObserver } from "@angular/cdk/layout";
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from "@angular/cdk/drag-drop";
import { CommonModule, NgComponentOutlet } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { Observable, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AlternativeContentConfig } from "src/app/shared/components/alternative-content";
import { PopoverComponent, DEFAULT_POPOVER_POSITIONS } from "./popover";
import {
  TreesloGridColumn,
  TreesloGridLoadCompletedEvent,
  TreesloGridLoadReason,
  TreesloGridLoadRequest,
  TreesloGridModel,
  TreesloGridSortOrder,
  TreesloGridSortSpec,
  ActionGridRow,
} from "./treeslo-grid.model";
import {
  cycleColumnSortState,
  getColumnSortFieldName,
} from "./helpers/treeslo-grid-sort.helper";
import {
  applyTreesloGridColumnOrder,
  isTreesloGridColumnDraggable,
} from "./helpers/treeslo-grid-column-order.helper";
import { TreesloGridActionMenuComponent } from "./treeslo-grid-action-menu/treeslo-grid-action-menu.component";
import { TreesloGridActionMenuHost } from "./treeslo-grid-action-menu/treeslo-grid-action-menu-host";
import { TableColumnTemplateDirective } from "./directives/table-column-template.directive";
import { TableRowDetailsTemplateDirective } from "./directives/table-row-details-template.directive";
import { TableResponsiveCardTemplateDirective } from "./directives/table-responsive-card-template.directive";
import { DynamicPipePipe } from "./pipes/dinamic-pipe.pipe";
import {
  PaginatorConfig,
  PaginatorState,
  TreesloPaginatorCursorBasedComponent,
} from "./treeslo-paginator-cursor-based/treeslo-paginator-cursor-based.component";
import { ORNAMENT_ASSETS } from "src/app/shared/constants/ornament-assets";

export type TreesloGridPageChangeEvent = {
  pageIndex: number;
  pageSize: number;
  cursor: string | null;
};

@Component({
  selector: "app-treeslo-grid",
  standalone: true,
  imports: [
    CommonModule,
    NgComponentOutlet,
    TranslateModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatMenuModule,
    TablerIconsModule,
    DynamicPipePipe,
    TreesloPaginatorCursorBasedComponent,
    TreesloGridActionMenuComponent,
    PopoverComponent,
    DragDropModule,
  ],
  templateUrl: "./treeslo-grid.component.html",
  styleUrls: ["./treeslo-grid.component.scss"],
})
export class TreesloGridComponent<ItemModel extends Record<string, any>>
  implements
    AfterContentInit,
    AfterViewInit,
    OnChanges,
    OnDestroy,
    TreesloGridActionMenuHost<ItemModel>
{
  @Input({ required: true }) config!: TreesloGridModel<ItemModel>;

  @ContentChildren(TableColumnTemplateDirective)
  columnTemplates!: QueryList<TableColumnTemplateDirective>;

  @ContentChild(TableRowDetailsTemplateDirective)
  rowDetailsTemplate?: TableRowDetailsTemplateDirective;

  @ContentChild(TableResponsiveCardTemplateDirective)
  responsiveCardTemplate?: TableResponsiveCardTemplateDirective;

  columnTemplateMap = new Map<string, TemplateRef<any>>();
  tableData: ItemModel[] = [];
  displayColumns: TreesloGridColumn<ItemModel>[] = [];
  activeSort: TreesloGridSortSpec[] = [];
  selectedRowIndexes = new Set<number>();
  expandedRowIndex: number | null = null;
  /** `config.expandedRowIndex`/`config.activeRowIndex` restore saved route state and must only apply on the very first load — reapplying them on every `refresh()`/filter reload would re-expand a row the user just collapsed. */
  private lastLoadReason: TreesloGridLoadReason = "initial";
  canExecuteCache = new Map<string, boolean>();
  private canExecuteSubscriptions: Map<string, Subscription> = new Map();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private static readonly MOBILE_QUERY = "(max-width: 768px)";
  isMobile = false;
  /** Bound to dynamicPipe so date/currency pipes refresh when the app language changes. */
  localeKey =
    this.translate.currentLang || this.translate.defaultLang || "en";
  readonly noDataImageSrc = ORNAMENT_ASSETS.noClaimsCases;
  private activeDropdownMenuTrigger: MatMenuTrigger | null = null;

  readonly defaultPopoverPositions = DEFAULT_POPOVER_POSITIONS;

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ lang }) => {
        this.localeKey = lang;
        this.cdr.markForCheck();
      });

    this.breakpointObserver
      .observe(TreesloGridComponent.MOBILE_QUERY)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.isMobile = state.matches;
        this.cdr.markForCheck();
      });
  }

  get responsiveCardsActive(): boolean {
    return !!this.config.allowResponsive && this.isMobile;
  }

  get hasResponsiveCardTemplate(): boolean {
    return !!this.responsiveCardTemplate?.templateRef;
  }

  get defaultCardColumns(): TreesloGridColumn<ItemModel>[] {
    return this.displayColumns.slice(0, 2);
  }

  get isColumnReorderActive(): boolean {
    return !!this.config?.reorderableColumns && !this.responsiveCardsActive;
  }

  get actionMenuHost(): TreesloGridActionMenuHost<ItemModel> {
    return this;
  }

  readonly gridAlternativeContentConfig: AlternativeContentConfig = {
    imageSrc: ORNAMENT_ASSETS.noClaimsCases,
    imageAlt: "No cases illustration",
    title: "No cases to display",
    description: "There are no items available for this table.",
  };

  @Output() selectedRowsChange = new EventEmitter<ItemModel[]>();
  @Output() pageChange = new EventEmitter<TreesloGridPageChangeEvent>();
  @Output() sortChange = new EventEmitter<TreesloGridSortSpec[]>();
  @Output() columnOrderChange = new EventEmitter<string[]>();
  @Output() loadCompleted =
    new EventEmitter<TreesloGridLoadCompletedEvent<ItemModel>>();

  @ViewChild(TreesloPaginatorCursorBasedComponent)
  private cursorPaginator?: TreesloPaginatorCursorBasedComponent;

  private loadSubscription?: Subscription;
  private trackedPageSize = 0;

  get hasPaginator(): boolean {
    return !!this.config.paginator;
  }

  get paginatorPageSizeOptions(): number[] {
    return this.config.paginator?.pageSizeOptions ?? [5, 10, 20, 50];
  }

  get paginatorInitialConfig(): PaginatorConfig | null {
    const paginator = this.config.paginator;
    if (!paginator) {
      return null;
    }

    const hasInitialState =
      paginator.pageIndex !== undefined ||
      paginator.pageSize !== undefined ||
      paginator.cursors !== undefined;

    if (!hasInitialState) {
      return null;
    }

    const defaultPageSize =
      paginator.pageSize ?? this.paginatorPageSizeOptions[0] ?? 10;

    return {
      pageIndex: paginator.pageIndex ?? 0,
      pageSize: this.resolvePageSize(defaultPageSize),
      cursors: paginator.cursors ?? [null],
    };
  }

  /** API query params from the embedded cursor paginator, if enabled. */
  get paginatorParams(): { limit: number; cursor: string | null } | null {
    return this.cursorPaginator?.paginatorParams ?? null;
  }

  /** Snapshot of paginator state for view-context / resolver restore. */
  get paginatorState(): PaginatorState | null {
    return this.cursorPaginator?.paginatorState ?? null;
  }

  onPaginatorPageChange(event: TreesloGridPageChangeEvent): void {
    const reason: TreesloGridLoadReason =
      event.pageSize !== this.trackedPageSize ? "pageSize" : "page";
    this.trackedPageSize = event.pageSize;
    this.triggerLoad(reason);
  }

  /** Reload data from page 0 (e.g. after parent filter changes). Resets cursor pagination. */
  refresh(): void {
    this.triggerLoad("refresh");
  }

  get sortState(): TreesloGridSortSpec[] {
    return [...this.activeSort];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["config"]) {
      this.syncDisplayColumns();
    }

    if (changes["config"]?.currentValue?.sort) {
      this.activeSort = [...(this.config.sort ?? [])];
    }
  }

  isColumnDraggable(column: TreesloGridColumn<ItemModel>): boolean {
    return this.isColumnReorderActive && isTreesloGridColumnDraggable(column);
  }

  onColumnDrop(event: CdkDragDrop<void>): void {
    if (!this.isColumnReorderActive || event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.displayColumns, event.previousIndex, event.currentIndex);
    this.columnOrderChange.emit(this.displayColumns.map((column) => column.fieldName));
    this.cdr.markForCheck();
  }

  private syncDisplayColumns(): void {
    if (!this.config?.columns) {
      this.displayColumns = [];
      return;
    }

    this.displayColumns = applyTreesloGridColumnOrder(
      this.config.columns,
      this.config.columnOrder,
    );
  }

  clearRowSelection(): void {
    this.selectedRowIndexes.clear();
    this.emitSelectionChange();
  }

  updatePaginatorAfterResponse(cursor: string | null): void {
    this.cursorPaginator?.updateAfterResponse(cursor);
  }

  resetPaginator(): void {
    this.cursorPaginator?.reset();
  }

  setPaginatorState(state: PaginatorConfig): void {
    this.cursorPaginator?.setState(state);
  }

  ngAfterContentInit(): void {
    this.syncDisplayColumns();
    this.buildColumnTemplateMap();
    this.columnTemplates.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildColumnTemplateMap();
        this.cdr.markForCheck();
      });
    this.validateActionsGridRows();
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    this.activeSort = [...(this.config.sort ?? [])];
    this.trackedPageSize = this.resolvePageSize(
      this.paginatorState?.pageSize ??
        this.config.paginator?.pageSize ??
        this.paginatorPageSizeOptions[0] ??
        10,
    );
    queueMicrotask(() => this.triggerLoad("initial"));
  }

  private triggerLoad(
    reason: TreesloGridLoadReason,
    requestedSort?: TreesloGridSortSpec[],
  ): void {
    this.lastLoadReason = reason;
    const load = this.config.load;

    if (reason === "refresh" || reason === "pageSize" || reason === "sort") {
      this.resetPaginator();
      if (reason === "pageSize") {
        this.trackedPageSize =
          this.paginatorState?.pageSize ?? this.trackedPageSize;
      }
    }

    const request = this.buildLoadRequest(reason, requestedSort);
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = load(request)
      .pipe(
        finalize(() => {
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (result) => {
          this.tableData = result.items ?? [];
          if (result.sort !== undefined) {
            this.activeSort = [...result.sort];
            this.sortChange.emit([...this.activeSort]);
          }
          this.expandedRowIndex = null;
          this.applySelectFirstRowOnLoad();
          if (!this.config.selectFirstRowOnLoad) {
            this.applyConfiguredExpandedRowIndex();
          }
          this.updatePaginatorAfterResponse(result.cursor ?? null);
          this.clearSelection();
          this.clearCanExecuteCache();
          this.loadCompleted.emit({ request, result });
          this.pageChange.emit({
            pageIndex: request.pagination.pageIndex,
            pageSize: request.pagination.pageSize,
            cursor: request.pagination.cursor,
          });
        },
        error: (error) => {
          console.error("TreesloGrid config.load failed:", error);
        },
      });
  }

  private buildLoadRequest(
    reason: TreesloGridLoadReason,
    requestedSort?: TreesloGridSortSpec[],
  ): TreesloGridLoadRequest {
    const params = this.paginatorParams;
    const state = this.paginatorState;
    const defaultPageSize = this.resolvePageSize(
      this.config.paginator?.pageSize ??
        this.paginatorPageSizeOptions[0] ??
        10,
    );
    const pageSize = this.resolvePageSize(
      state?.pageSize && state.pageSize > 0 ? state.pageSize : defaultPageSize,
    );

    return {
      reason,
      pagination: {
        kind: "cursor",
        limit: params?.limit && params.limit > 0 ? params.limit : pageSize,
        cursor: params?.cursor ?? null,
        pageIndex: state?.pageIndex ?? 0,
        pageSize,
      },
      sort: [...(requestedSort ?? this.activeSort)],
    };
  }

  private resolvePageSize(size: number): number {
    const options = this.paginatorPageSizeOptions;
    if (!options.length) {
      return size || 10;
    }

    if (options.includes(size)) {
      return size;
    }

    return options.reduce((closest, option) =>
      Math.abs(option - size) < Math.abs(closest - size) ? option : closest,
    );
  }

  private validateActionsGridRows(): void {
    if (!this.config.actionsGridRows?.length) {
      return;
    }

    for (const action of this.config.actionsGridRows) {
      this.validateActionRow(action);
    }
  }

  private validateActionRow(action: ActionGridRow<ItemModel>): void {
    const isDropdown = action.dropdownMode === true;

    if (isDropdown) {
      if (!action.dropdownChildrenActions?.length) {
        throw new Error(
          "TreesloGrid: action with dropdownMode=true must define a non-empty dropdownChildrenActions array.",
        );
      }

      if (action.action) {
        throw new Error(
          "TreesloGrid: action with dropdownMode=true must not define an action at the same level. Use dropdownChildrenActions instead.",
        );
      }

      action.dropdownChildrenActions.forEach((child) =>
        this.validateActionRow(child),
      );
    } else {
      if (!action.action) {
        throw new Error(
          "TreesloGrid: non-dropdown action must define an action function when dropdownMode is false or undefined.",
        );
      }
    }
  }

  get activeRowIndex(): number | null | undefined {
    return this.config.activeRowIndex;
  }

  set activeRowIndex(value: number | null | undefined) {
    this.config.activeRowIndex = value ?? null;
  }

  /** Re-apply `selectFirstRowOnLoad` after the parent mutates `tableData` directly. */
  syncSelectFirstRowOnLoad(): void {
    this.applySelectFirstRowOnLoad();
    this.cdr.markForCheck();
  }

  private applySelectFirstRowOnLoad(): void {
    if (!this.config.selectFirstRowOnLoad) {
      return;
    }

    if (this.tableData.length === 0) {
      this.activeRowIndex = null;
      this.expandedRowIndex = null;
      this.cdr.markForCheck();
      return;
    }

    const configuredIndex = this.config.activeRowIndex;
    const hasConfiguredIndex =
      configuredIndex !== null &&
      configuredIndex !== undefined &&
      configuredIndex >= 0 &&
      configuredIndex < this.tableData.length;

    this.activeRowIndex = hasConfiguredIndex ? configuredIndex : 0;
    this.applyConfiguredExpandedRowIndex();
    this.cdr.markForCheck();
  }

  private applyConfiguredExpandedRowIndex(): void {
    if (this.lastLoadReason !== "initial") {
      return;
    }

    const configuredExpanded = this.config.expandedRowIndex;
    if (
      configuredExpanded === null ||
      configuredExpanded === undefined ||
      configuredExpanded < 0 ||
      configuredExpanded >= this.tableData.length
    ) {
      return;
    }

    this.expandedRowIndex = configuredExpanded;
  }

  onRowClick(index: number, event: MouseEvent): void {
    if (this.config.multirowSelection) {
      if (event.ctrlKey || event.metaKey) {
        this.toggleRowSelection(index, true);
        return;
      }
      this.activeRowIndex = index;
      return;
    }

    this.activeRowIndex = index;
  }

  private buildColumnTemplateMap(): void {
    this.columnTemplateMap.clear();
    this.columnTemplates?.forEach((templateDir) => {
      this.columnTemplateMap.set(
        templateDir.appTableColumnTemplate,
        templateDir.templateRef,
      );
    });
  }

  private clearSelection(): void {
    if (
      this.config.multirowSelection &&
      this.config.selectedRowIndexes?.length
    ) {
      this.selectedRowIndexes.clear();
      for (const index of this.config.selectedRowIndexes) {
        if (
          Number.isInteger(index) &&
          index >= 0 &&
          index < this.tableData.length
        ) {
          this.selectedRowIndexes.add(index);
        }
      }
    } else {
      this.selectedRowIndexes.clear();
    }

    this.emitSelectionChange();
  }

  hasActions(): boolean {
    return !!(
      this.config.actionsGridRows && this.config.actionsGridRows.length > 0
    );
  }

  isColumnSortable(column: TreesloGridColumn<ItemModel>): boolean {
    return !!column.sortable;
  }

  isColumnSortControlVisible(column: TreesloGridColumn<ItemModel>): boolean {
    return this.isColumnSortable(column) && this.tableData.length > 1;
  }

  onSortHeaderClick(column: TreesloGridColumn<ItemModel>, event: Event): void {
    event.stopPropagation();
    if (!this.isColumnSortControlVisible(column)) {
      return;
    }

    const fieldName = getColumnSortFieldName(column);
    const maxFields = this.config.maxSortFields ?? 3;
    const requestedSort = cycleColumnSortState(
      this.activeSort,
      fieldName,
      maxFields,
    );
    this.triggerLoad("sort", requestedSort);
  }

  getColumnSortOrder(column: TreesloGridColumn<ItemModel>): TreesloGridSortOrder | null {
    const fieldName = getColumnSortFieldName(column);
    return (
      this.activeSort.find((entry) => entry.fieldName === fieldName)?.order ??
      null
    );
  }

  getColumnSortPriority(column: TreesloGridColumn<ItemModel>): number | null {
    if (this.activeSort.length <= 1) {
      return null;
    }

    const fieldName = getColumnSortFieldName(column);
    const index = this.activeSort.findIndex(
      (entry) => entry.fieldName === fieldName,
    );
    return index >= 0 ? index + 1 : null;
  }

  getColumnSortIcon(column: TreesloGridColumn<ItemModel>): string {
    const order = this.getColumnSortOrder(column);
    if (order === "asc") {
      return "arrow-up";
    }
    if (order === "desc") {
      return "arrow-down";
    }
    return "arrows-sort";
  }

  getColumnSortAriaLabel(column: TreesloGridColumn<ItemModel>): string {
    const header = this.translate.instant(
      this.getTranslationKey(this.getHeaderValue(column)),
    );
    const order = this.getColumnSortOrder(column);
    const priority = this.getColumnSortPriority(column);

    if (order === "asc") {
      return priority
        ? this.translate.instant("common.sortColumnAscPriority", {
            column: header,
            priority,
          })
        : this.translate.instant("common.sortColumnAsc", { column: header });
    }

    if (order === "desc") {
      return priority
        ? this.translate.instant("common.sortColumnDescPriority", {
            column: header,
            priority,
          })
        : this.translate.instant("common.sortColumnDesc", { column: header });
    }

    return this.translate.instant("common.sortColumnNone", { column: header });
  }

  getHeaderValue(column: TreesloGridColumn<ItemModel>): string {
    return column.displayfieldName;
  }

  getHeaderLabel(column: TreesloGridColumn<ItemModel>): string {
    const full = this.translate.instant(
      this.getTranslationKey(this.getHeaderValue(column)),
    );
    return this.truncateAtText(full, column.truncateAt);
  }

  getHeaderLabelTooltip(
    column: TreesloGridColumn<ItemModel>,
  ): string | null {
    const full = this.translate.instant(
      this.getTranslationKey(this.getHeaderValue(column)),
    );

    if (column.truncateAt && full.length > column.truncateAt) {
      return full;
    }

    return null;
  }

  getTranslationKey(key: string): string {
    if (this.config.translationPrefix) {
      return `${this.config.translationPrefix}.${key}`;
    }
    return key;
  }

  getColumnAlignmentClass(column: TreesloGridColumn<ItemModel>): string {
    switch (column.align) {
      case "center":
        return "align-center";
      case "right":
        return "align-right";
      default:
        return "align-left";
    }
  }

  getCellValue(row: ItemModel, column: TreesloGridColumn<ItemModel>): any {
    return row[column.fieldName];
  }

  getWidgetComponent(
    column: TreesloGridColumn<ItemModel>,
    row?: ItemModel,
  ): Type<any> | null {
    if (!column.widget || !row) {
      return null;
    }

    const widgetDef = column.widget(row);
    return widgetDef?.widgetComponent ?? null;
  }

  getWidgetInputs(
    row: ItemModel,
    column: TreesloGridColumn<ItemModel>,
  ): Record<string, unknown> {
    if (!column.widget) {
      return { value: row[column.fieldName] };
    }

    const widgetDef = column.widget(row);
    const widgetConfig = widgetDef?.configs ?? {};
    const inputs: Record<string, unknown> = {
      ...widgetConfig,
      value: row[column.fieldName],
    };

    if (this.config.translationPrefix) {
      inputs["translationPrefix"] = this.config.translationPrefix;
    }

    return inputs;
  }

  getActionLabel(action: ActionGridRow<ItemModel>): string {
    return action.label ? this.getTranslationKey(action.label) : "";
  }

  getActionDisplayValue(action: ActionGridRow<ItemModel>): string {
    return action.label ? this.getTranslationKey(action.label) : "";
  }

  getActionMenuLabel(action: ActionGridRow<ItemModel>): string {
    const key = action.labelMobile ?? action.label;
    return key ? this.getTranslationKey(key) : "";
  }

  getActionTooltip(action: ActionGridRow<ItemModel>, row: ItemModel): string {
    const tooltipValue =
      typeof action.tooltip === "function"
        ? action.tooltip(row)
        : action.tooltip;
    if (tooltipValue) {
      return this.getTranslationKey(tooltipValue);
    }
    return "";
  }

  private static readonly MOBILE_ACTION_OVERFLOW_THRESHOLD = 3;

  getVisibleTopLevelActionCount(row: ItemModel): number {
    if (!this.config.actionsGridRows?.length) {
      return 0;
    }

    return this.config.actionsGridRows.filter((action) =>
      action.dropdownMode
        ? this.isDropdownActionVisible(action, row)
        : this.isActionVisible(action, row),
    ).length;
  }

  shouldCollapseRowActions(row: ItemModel): boolean {
    return (
      this.isMobile &&
      this.getVisibleTopLevelActionCount(row) >
        TreesloGridComponent.MOBILE_ACTION_OVERFLOW_THRESHOLD
    );
  }

  getMobileOverflowDropdownAction(row: ItemModel): ActionGridRow<ItemModel> {
    const children = (this.config.actionsGridRows ?? []).filter((action) =>
      action.dropdownMode
        ? this.isDropdownActionVisible(action, row)
        : this.isActionVisible(action, row),
    );

    return {
      icon: "dots-vertical",
      tooltip: "Actions",
      dropdownMode: true,
      dropdownChildrenActions: children,
    };
  }

  getColumnTemplate(
    column: TreesloGridColumn<ItemModel>,
  ): TemplateRef<any> | null {
    return this.columnTemplateMap.get(column.fieldName) ?? null;
  }

  hasColumnPopover(column: TreesloGridColumn<ItemModel>): boolean {
    return !!column.popover;
  }

  isColumnPopoverDisabled(
    column: TreesloGridColumn<ItemModel>,
    row: ItemModel,
  ): boolean {
    return column.popover?.disabled?.(row) ?? false;
  }

  isColumnPopoverActive(
    column: TreesloGridColumn<ItemModel>,
    row: ItemModel,
  ): boolean {
    return this.hasColumnPopover(column) && !this.isColumnPopoverDisabled(column, row);
  }

  getColumnPopoverTrigger(column: TreesloGridColumn<ItemModel>): "click" | "hover" {
    return column.popover?.trigger ?? "click";
  }

  getColumnPopoverContent(
    column: TreesloGridColumn<ItemModel>,
    row: ItemModel,
  ): string | undefined {
    const content = column.popover?.content;

    if (typeof content === "function") {
      return content(row);
    }

    return content;
  }

  getColumnPopoverBodyInputs(
    column: TreesloGridColumn<ItemModel>,
    row: ItemModel,
    popover?: PopoverComponent,
  ): Record<string, unknown> {
    const inputs = column.popover?.bodyInputs?.(row) ?? {};

    return {
      ...inputs,
      closePopover: () => popover?.close(),
    };
  }

  isEmptyValue(row: ItemModel, column: TreesloGridColumn<ItemModel>): boolean {
    const value = this.getCellValue(row, column);
    return value === null || value === undefined || String(value).trim() === "";
  }

  executeAction(
    action: ActionGridRow<ItemModel>,
    row: ItemModel,
    event: Event,
    rowIndex?: number,
  ): void {
    event.stopPropagation();
    if (rowIndex !== undefined) {
      this.activeRowIndex = rowIndex;
    }
    if (!action.action) {
      return;
    }
    action.action(row);
    this.closeActiveDropdownMenu();
  }

  onDropdownMenuClosed(): void {
    this.activeDropdownMenuTrigger = null;
  }

  private closeActiveDropdownMenu(): void {
    if (this.activeDropdownMenuTrigger?.menuOpen) {
      this.activeDropdownMenuTrigger.closeMenu();
    }
  }

  onDropdownMenuOpened(menuTrigger: MatMenuTrigger): void {
    this.activeDropdownMenuTrigger = menuTrigger;
  }

  onRowActionMenuClick(event: Event, stopRowClick: boolean): void {
    if (stopRowClick) {
      event.stopPropagation();
    }
  }

  getVisibleDropdownActions(
    actions: ActionGridRow<ItemModel>[],
    row: ItemModel,
  ): ActionGridRow<ItemModel>[] {
    return actions
      .map((child) => {
        if (child.dropdownMode) {
          const visibleChildren = this.getVisibleDropdownActions(
            child.dropdownChildrenActions ?? [],
            row,
          );
          if (!visibleChildren.length) {
            return null;
          }
          return {
            ...child,
            dropdownChildrenActions: visibleChildren,
          };
        }
        return this.isActionVisible(child, row) ? child : null;
      })
      .filter(
        (child): child is ActionGridRow<ItemModel> =>
          child !== null && child !== undefined,
      );
  }

  isActionDisabled(action: ActionGridRow<ItemModel>, row: ItemModel): boolean {
    if (action.dropdownMode) {
      return this.isDropdownActionDisabled(action, row);
    }

    if (typeof action.disabled === "function") {
      const result = action.disabled(row);
      if (result instanceof Observable) {
        return false; // Observable results are not handled synchronously
      }
      return result;
    }
    return !!action.disabled;
  }

  isActionVisible(action: ActionGridRow<ItemModel>, row: ItemModel): boolean {
    if (action.dropdownMode) {
      return this.isDropdownActionVisible(action, row);
    }

    if (typeof action.canExecute === "function") {
      const result = action.canExecute(row);
      if (result instanceof Observable) {
        return true; // Show while observable is resolving
      }
      return result;
    }
    return true; // By default, action is visible
  }

  isDropdownActionVisible(
    action: ActionGridRow<ItemModel>,
    row: ItemModel,
  ): boolean {
    if (!action.dropdownMode || !action.dropdownChildrenActions?.length) {
      return false;
    }

    return action.dropdownChildrenActions.some((child) =>
      child.dropdownMode
        ? this.isDropdownActionVisible(child, row)
        : this.isActionVisible(child, row),
    );
  }

  isDropdownActionDisabled(
    action: ActionGridRow<ItemModel>,
    row: ItemModel,
  ): boolean {
    if (!action.dropdownMode || !action.dropdownChildrenActions?.length) {
      return true;
    }

    return action.dropdownChildrenActions.every(
      (child) =>
        !this.isActionVisible(child, row) || this.isActionDisabled(child, row),
    );
  }

  private subscribeToCanExecute(
    actionIndex: number,
    rowIndex: number,
    action: ActionGridRow<ItemModel>,
    row: ItemModel,
  ): void {
    const result = action.canExecute?.(row);
    if (result instanceof Observable) {
      const cacheKey = `${rowIndex}_${actionIndex}`;
      // Unsubscribe from previous subscription if it exists
      const prevSub = this.canExecuteSubscriptions.get(cacheKey);
      if (prevSub) {
        prevSub.unsubscribe();
      }

      // Subscribe to the new observable
      const subscription = result.subscribe((canExecute) => {
        this.canExecuteCache.set(cacheKey, canExecute);
        this.cdr.markForCheck();
      });

      this.canExecuteSubscriptions.set(cacheKey, subscription);
    }
  }

  private clearCanExecuteCache(): void {
    this.canExecuteCache.clear();
    this.canExecuteSubscriptions.forEach((sub) => sub.unsubscribe());
    this.canExecuteSubscriptions.clear();
  }

  isRowSelected(rowIndex: number): boolean {
    return this.selectedRowIndexes.has(rowIndex);
  }

  toggleRowSelection(rowIndex: number, activateRow = false): void {
    if (activateRow) {
      this.activeRowIndex = rowIndex;
    }

    if (this.selectedRowIndexes.has(rowIndex)) {
      this.selectedRowIndexes.delete(rowIndex);
    } else {
      this.selectedRowIndexes.add(rowIndex);
    }
    this.emitSelectionChange();
  }

  toggleAllRows(checked: boolean): void {
    this.selectedRowIndexes.clear();
    if (checked) {
      this.tableData.forEach((_, index) => this.selectedRowIndexes.add(index));
    }
    this.emitSelectionChange();
  }

  isAllSelected(): boolean {
    return (
      this.tableData.length > 0 &&
      this.selectedRowIndexes.size === this.tableData.length
    );
  }

  isSomeSelected(): boolean {
    return (
      this.selectedRowIndexes.size > 0 &&
      this.selectedRowIndexes.size < this.tableData.length
    );
  }

  hasActionsOrDetails(): boolean {
    return !!(
      this.hasActions() ||
      (this.config.rowDetails && !!this.rowDetailsTemplate?.templateRef)
    );
  }

  isRowExpanded(rowIndex: number): boolean {
    return this.expandedRowIndex === rowIndex;
  }

  toggleRowDetails(rowIndex: number): void {
    this.expandedRowIndex =
      this.expandedRowIndex === rowIndex ? null : rowIndex;
  }

  private emitSelectionChange(): void {
    const selectedItems = this.tableData.filter((_, index) =>
      this.selectedRowIndexes.has(index),
    );
    this.selectedRowsChange.emit(selectedItems);
  }

  formatCellValue(
    row: ItemModel,
    column: TreesloGridColumn<ItemModel>,
  ): string {
    const value = this.getCellValue(row, column);
    if (value === null || value === undefined || value === "") {
      return "---";
    }
    const stringValue = String(value);
    return this.truncateAtText(stringValue, column.truncateAt);
  }

  private truncateAtText(value: string, truncateAt?: number): string {
    if (truncateAt && value.length > truncateAt) {
      return `${value.substring(0, truncateAt)}...`;
    }

    return value;
  }

  getColumnTrackId(column: TreesloGridColumn<ItemModel>): string {
    return column.fieldName || column.displayfieldName;
  }

  /** Stable row track id; index suffix avoids NG0955 when API ids repeat on a page. */
  getRowTrackId(row: ItemModel, index: number): string | number {
    return `${this.getTrackId(row)}::${index}`;
  }

  getActionTrackId(index: number, action: ActionGridRow<ItemModel>): string {
    return action.icon || action.label || `action-${index}`;
  }

  /**
   * Get a unique identifier for tracking rows in *ngFor
   * Searches for common ID properties: id, claimId, caseId, referenceId, fnsku, etc.
   */
  getTrackId(row: ItemModel): string | number {
    if (!row) return Math.random(); // Fallback for null/undefined

    if ("reimbursementId" in row && row["reimbursementId"]) {
      const reimbursementId = String(row["reimbursementId"]);
      const isInventoryReturn = Boolean(row["isInventoryReturn"]);
      const type = row["type"] != null ? String(row["type"]) : "";
      const quantity = row["quantity"] != null ? String(row["quantity"]) : "";
      const approvalDate =
        row["approvalDate"] != null ? String(row["approvalDate"]) : "";
      const amount = row["amount"] != null ? String(row["amount"]) : "";

      return [
        reimbursementId,
        isInventoryReturn,
        type,
        quantity,
        approvalDate,
        amount,
      ].join(":");
    }

    // List of common ID property names to check
    const idProperties = [
      "id",
      "claimId",
      "caseId",
      "providerCaseId",
      "referenceId",
      "fnsku",
      "sku",
      "cogsId",
      "providerId",
      "storeId",
    ];

    // Find the first property that exists and has a value
    for (const prop of idProperties) {
      if (prop in row && row[prop]) {
        return row[prop] as string | number;
      }
    }

    // If no ID property found, return a stringified version of the entire object as a fallback
    // This is less efficient but still better than tracking by reference
    return JSON.stringify(row);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.clearCanExecuteCache();
  }
}
